import React, { useState, useEffect } from 'react';
import { Settings } from 'lucide-react';
import { toast } from 'react-hot-toast';
import AppBuilderProjectForm from './AppBuilderProjectForm';
import AppBuilderProgressBar from './AppBuilderProgressBar';
import AppBuilderQRCodeDisplay from './AppBuilderQRCodeDisplay';
import EditorialShell from './EditorialShell';
import StepHeading from './StepHeading';
import { AppBuilderProjectRequest, AppBuilderProjectResponse, AppBuilderProjectStatus, AppBuilderProgressUpdate } from '../types/appbuilder';
import { appBuilderApiService } from '../services/appBuilderApi';

// localStorage utilities for project state management
const PROJECT_STORAGE_KEY = 'activeAppBuilderProject';

const saveProjectToStorage = (project: AppBuilderProjectResponse) => {
  try {
    localStorage.setItem(PROJECT_STORAGE_KEY, JSON.stringify({
      project_id: project.project_id,
      project_name: project.project_name,
      status: project.status,
      progress: project.progress,
      message: project.message,
      github_url: project.github_url,
      live_url: project.live_url,
      qr_code: project.qr_code,
      created_at: project.created_at,
      completed_at: project.completed_at,
      saved_at: Date.now()
    }));
  } catch (error) {
    console.warn('Failed to save AppBuilder project to localStorage:', error);
  }
};

const getProjectFromStorage = (): AppBuilderProjectResponse | null => {
  try {
    const stored = localStorage.getItem(PROJECT_STORAGE_KEY);
    if (!stored) return null;
    
    const parsed = JSON.parse(stored);
    // Check if stored project is not too old (24 hours)
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours
    if (Date.now() - parsed.saved_at > maxAge) {
      clearProjectFromStorage();
      return null;
    }
    
    return parsed;
  } catch (error) {
    console.warn('Failed to load AppBuilder project from localStorage:', error);
    clearProjectFromStorage();
    return null;
  }
};

const clearProjectFromStorage = () => {
  try {
    localStorage.removeItem(PROJECT_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear AppBuilder project from localStorage:', error);
  }
};

function AppBuilderApp() {
  const [currentProject, setCurrentProject] = useState<AppBuilderProjectResponse | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [websocket, setWebsocket] = useState<WebSocket | null>(null);

  // Check for stored project on app load and attempt reconnection
  useEffect(() => {
    const restoreProject = async () => {
      const storedProject = getProjectFromStorage();
      if (!storedProject) return;

      console.log('Found stored AppBuilder project:', storedProject);
      
      // If project is already completed, just restore it
      if (storedProject.status === AppBuilderProjectStatus.COMPLETED || storedProject.status === AppBuilderProjectStatus.FAILED) {
        setCurrentProject(storedProject);
        return;
      }

      // For in-progress projects, restore state and reconnect
      setCurrentProject(storedProject);
      setIsGenerating(true);
      
      try {
        // Try to fetch fresh project data from server
        const freshProject = await appBuilderApiService.getProject(storedProject.project_id);
        setCurrentProject(freshProject);
        
        // If still in progress, reconnect WebSocket
        if (freshProject.status !== AppBuilderProjectStatus.COMPLETED && freshProject.status !== AppBuilderProjectStatus.FAILED) {
          toast.success('Reconnecting to your AppBuilder project...');
          await connectWebSocket(storedProject.project_id);
        } else {
          setIsGenerating(false);
          if (freshProject.status === AppBuilderProjectStatus.COMPLETED) {
            toast.success('Your AppBuilder project completed while you were away! 🎉');
          }
        }
      } catch (error) {
        console.error('Failed to restore AppBuilder project:', error);
        toast.error('Failed to reconnect to your AppBuilder project. Starting fresh.');
        clearProjectFromStorage();
        setCurrentProject(null);
        setIsGenerating(false);
      }
    };

    restoreProject();
  }, []);

  useEffect(() => {
    return () => {
      // Cleanup websocket on unmount
      if (websocket) {
        websocket.close();
      }
    };
  }, [websocket]);

  const connectWebSocket = (projectId: string) => {
    return new Promise<WebSocket>((resolve, reject) => {
      const wsUrl = appBuilderApiService.getWebSocketUrl(projectId);
      const ws = new WebSocket(wsUrl);

      // Heartbeat mechanism to keep connection alive
      let heartbeatInterval: NodeJS.Timeout;

      ws.onopen = () => {
        console.log('AppBuilder WebSocket connected');
        
        // Start heartbeat - send ping every 30 seconds
        heartbeatInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000);

        resolve(ws);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('AppBuilder WebSocket message received:', data);
          
          // Handle different message types
          if (data.project_id === projectId || data.project_id) {
            setCurrentProject(prev => {
              console.log('Updating AppBuilder project state:', { prev, data });
              const updatedProject = !prev ? data : { ...prev, ...data };
              
              // Save updated project to localStorage
              saveProjectToStorage(updatedProject);
              
              return updatedProject;
            });

            // Show notifications for major milestones
            if (data.status === AppBuilderProjectStatus.COMPLETED) {
              toast.success('Your AppBuilder app is ready! 🎉');
              setIsGenerating(false);
              
              // Fetch complete project data as fallback to ensure we have URLs and QR code
              if (!data.live_url || !data.qr_code) {
                console.log('Missing AppBuilder project data, fetching from API...');
                setTimeout(async () => {
                  try {
                    const completeProject = await appBuilderApiService.getProject(data.project_id || projectId);
                    setCurrentProject(completeProject);
                    saveProjectToStorage(completeProject);
                    console.log('Fetched complete AppBuilder project data:', completeProject);
                  } catch (error) {
                    console.error('Failed to fetch complete AppBuilder project data:', error);
                  }
                }, 1000);
              }
            } else if (data.status === AppBuilderProjectStatus.FAILED) {
              toast.error('AppBuilder generation failed. Please try again.');
              setIsGenerating(false);
            }
          }
        } catch (error) {
          console.error('Failed to parse AppBuilder WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('AppBuilder WebSocket error:', error);
        toast.error('Connection error. Please refresh the page.');
        if (heartbeatInterval) {
          clearInterval(heartbeatInterval);
        }
        reject(error);
      };

      ws.onclose = () => {
        console.log('AppBuilder WebSocket disconnected');
        if (heartbeatInterval) {
          clearInterval(heartbeatInterval);
        }
        setIsGenerating(false);
      };

      setWebsocket(ws);
      
      // Timeout after 5 seconds if connection fails
      setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          reject(new Error('AppBuilder WebSocket connection timeout'));
        }
      }, 5000);
    });
  };

  const handleProjectSubmit = async (request: AppBuilderProjectRequest) => {
    try {
      setIsGenerating(true);
      
      // Create project
      const project = await appBuilderApiService.createProject(request);
      setCurrentProject(project);
      
      // Save initial project state to localStorage
      saveProjectToStorage(project);

      // Connect to WebSocket for real-time updates and wait for connection
      try {
        await connectWebSocket(project.project_id);
        toast.success('Connected! Generating your AppBuilder project...');
      } catch (wsError) {
        console.error('AppBuilder WebSocket connection failed:', wsError);
        toast.error('Real-time updates unavailable, but generation will continue.');
      }

    } catch (error: any) {
      console.error('Failed to create AppBuilder project:', error);
      toast.error(error.message || 'Failed to create AppBuilder project. Please try again.');
      setIsGenerating(false);
    }
  };

  const handleStartNew = () => {
    if (websocket) {
      websocket.close();
      setWebsocket(null);
    }
    setCurrentProject(null);
    setIsGenerating(false);
    
    // Clear stored project when starting new
    clearProjectFromStorage();
  };

  const isProjectComplete = currentProject?.status === AppBuilderProjectStatus.COMPLETED;
  const isProjectFailed = currentProject?.status === AppBuilderProjectStatus.FAILED;

  const phase = !currentProject ? 1 : isProjectComplete ? 3 : 2;

  return (
    <EditorialShell
      studio="builder"
      chapter="App Builder"
      steps={[{ label: 'The Brief' }, { label: 'Build' }, { label: 'Delivery' }]}
      currentStep={phase}
    >
        {!currentProject && (
          <AppBuilderProjectForm onSubmit={handleProjectSubmit} isLoading={isGenerating} />
        )}

        {currentProject && !isProjectComplete && !isProjectFailed && (
          <div className="ed-enter">
            <StepHeading
              index={2}
              kicker="In build"
              title="Writing your application."
              lede="Planning the structure, generating the code, committing it, and deploying it to a live URL."
            />

            <div className="ed-block">
              <AppBuilderProgressBar
                progress={currentProject.progress}
                status={currentProject.status}
                message={currentProject.message}
              />

              <div className="ed-actions">
                <button type="button" className="ed-btn ed-btn--bare" onClick={handleStartNew}>
                  Cancel and start over
                </button>
              </div>
            </div>
          </div>
        )}

        {currentProject && isProjectFailed && (
          <div className="ed-enter">
            <StepHeading
              index={2}
              kicker="Build failed"
              title="That one didn't make it."
              lede="Something went wrong during generation. Rewording the brief usually helps."
            />

            <div className="ed-block">
              <AppBuilderProgressBar
                progress={currentProject.progress}
                status={currentProject.status}
                message={currentProject.error_message || currentProject.message}
                animated={false}
              />

              <div className="ed-actions">
                <button type="button" className="ed-btn ed-btn--primary" onClick={handleStartNew}>
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {currentProject && isProjectComplete && (
          <div className="ed-enter">
            <StepHeading
              index={3}
              kicker="Delivered"
              title={currentProject.project_name}
              accent="is live."
              lede="Built, committed, and deployed. Open it, share it, or scan the code to try it on your phone."
            />

            {currentProject.live_url ? (
              <AppBuilderQRCodeDisplay
                url={currentProject.live_url}
                qrCode={currentProject.qr_code}
                projectName={currentProject.project_name}
              />
            ) : (
              <div className="ed-empty">
                <span className="ed-empty-icon" aria-hidden="true">
                  <Settings className="ed-icon-spin" />
                </span>
                <h3 className="ed-empty-title">Finishing up…</h3>
                <p className="ed-body">The build completed and we are loading the final details.</p>
              </div>
            )}

            <div className="ed-block">
              <div className="ed-actions">
                <button type="button" className="ed-btn ed-btn--lg" onClick={handleStartNew}>
                  Build another
                </button>
              </div>
            </div>
          </div>
        )}
    </EditorialShell>
  );
}

export default AppBuilderApp;
